import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Apple, Play, Bell, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function AppStorePreview() {
  const { t } = useTranslation();
  return (
    <section
      className="relative py-24 md:py-32 bg-canvas overflow-hidden"
      data-testid="app-store-preview"
    >
      <div className="container-x">
        <div className="rounded-[36px] bg-forest text-canvas noise relative overflow-hidden">
          {/* Ambient emerald glow */}
          <div className="pointer-events-none absolute -right-20 top-1/3 w-[520px] h-[520px] rounded-full bg-lime/12 blur-[100px]" />
          <div className="pointer-events-none absolute -left-40 -bottom-24 w-[460px] h-[460px] rounded-full bg-lime/8 blur-[110px]" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 p-8 md:p-14 relative">
            {/* Copy */}
            <div className="lg:col-span-7">
              <div className="label-eyebrow !text-lime">{t("home.appStore.eyebrow")}</div>
              <h2 className="mt-5 font-display font-medium text-canvas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
                iOS &amp; Android,<br />
                <span className="italic font-serif text-lime">on the same day.</span>
              </h2>
              <p className="mt-6 text-canvas/75 leading-relaxed max-w-lg">
                No progressive rollout, no country-locked builds. When Instantxfer CashApp ships, it ships everywhere at once — with Face-ID unlock, offline receipts, and support in nine languages from day one.
              </p>

              {/* Badges */}
              <div className="mt-10 flex flex-wrap gap-3" data-testid="store-badges">
                <StoreBadge
                  brand="ios"
                  tagline="Download on the"
                  store="App Store"
                  testId="badge-ios"
                />
                <StoreBadge
                  brand="android"
                  tagline="Get it on"
                  store="Google Play"
                  testId="badge-android"
                />
              </div>

              {/* Meta row */}
              <div className="mt-8 grid grid-cols-3 max-w-md gap-6">
                <Meta k="9" label="Languages" />
                <Meta k="14+" label="Age rating" />
                <Meta k="< 30MB" label="Install size" />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Link to="/waitlist" className="btn-accent" data-testid="app-store-notify">
                  <Bell className="w-4 h-4" strokeWidth={2} />
                  Notify me on launch day
                </Link>
                <span className="text-xs text-canvas/60">
                  Founder 100 members get TestFlight access two weeks early
                </span>
              </div>
            </div>

            {/* Right — floating device tiles */}
            <div className="lg:col-span-5 relative min-h-[420px]">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <DeviceTile />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StoreBadge({ brand, tagline, store, testId }) {
  return (
    <a
      href="#waitlist"
      data-testid={testId}
      className="group inline-flex items-center gap-4 rounded-2xl border border-canvas/25 bg-canvas/[0.04] backdrop-blur-sm px-5 py-3.5 hover:bg-canvas/[0.08] hover:border-lime/60 transition-all duration-300"
    >
      <div className="w-9 h-9 rounded-xl bg-canvas text-forest grid place-items-center shrink-0 group-hover:bg-lime transition-colors">
        {brand === "ios" ? (
          <Apple className="w-4 h-4" strokeWidth={2} fill="currentColor" />
        ) : (
          <Play className="w-4 h-4" strokeWidth={2} fill="currentColor" />
        )}
      </div>
      <div className="text-left">
        <div className="text-[10px] tracking-[0.22em] uppercase text-canvas/60 font-medium">
          {tagline}
        </div>
        <div className="font-display text-lg text-canvas tracking-tight leading-tight">
          {store}
        </div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-canvas/40 group-hover:text-lime transition-colors ml-1" />
    </a>
  );
}

function Meta({ k, label }) {
  return (
    <div>
      <div className="font-display text-2xl md:text-3xl tracking-tightest text-canvas">{k}</div>
      <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-canvas/50">{label}</div>
    </div>
  );
}

function DeviceTile() {
  return (
    <div className="relative w-full max-w-[280px] aspect-square">
      {/* Back card */}
      <div className="absolute inset-0 rounded-3xl bg-canvas/[0.04] border border-canvas/15 rotate-6" />
      {/* Front card */}
      <div className="absolute inset-0 rounded-3xl bg-canvas -rotate-3 overflow-hidden shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]">
        <div className="p-6 h-full flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="w-12 h-12 rounded-2xl bg-forest grid place-items-center relative overflow-hidden">
              <svg width="26" height="26" viewBox="0 0 30 30" fill="none">
                <path
                  d="M16.4 5.5 L8.8 16.2 h4.7 L11.4 24.5 L21.2 12.4 h-4.9 z"
                  fill="#E8FF00"
                  stroke="#E8FF00"
                  strokeWidth="0.4"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="text-[10px] tracking-[0.22em] uppercase text-ink/50 font-medium">
              4.9 ★ TestFlight
            </span>
          </div>
          <div>
            <div className="font-display text-2xl text-forest tracking-tight">Instantxfer CashApp</div>
            <div className="mt-1 text-sm text-ink/60">Money Transfer &amp; Wallet</div>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-[10px] tracking-[0.22em] uppercase text-forest bg-lime px-2 py-1 rounded-full font-bold">
                Pre-order
              </span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-ink/40">Free</span>
            </div>
          </div>
        </div>
      </div>
      {/* Emerald glow orb */}
      <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-lime/40 blur-2xl" />
    </div>
  );
}
